/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NavigationModule
 */

import {
    ChangeDetectionStrategy,
    Component,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import * as StateNames from 'state-names';
import * as globalL10n from 'global-l10n';
import { INavState } from '../../navigation.types';
import { NavigationService } from '../../services/navigation.service';
import * as l10n from './templates-vertical-nav.component.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Vertical Navigation component for the Operations pages.
 * @author alextsg, Zhiqian Liu
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'templates-vertical-nav',
    template: '<vertical-nav [states]="templatesStates"></vertical-nav>',
})
export class TemplatesVerticalNavComponent {
    /**
     * List of navigation states and children that should be shown in the nav.
     */
    public templatesStates: INavState[] = [];

    constructor(
        navigationService: NavigationService,
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        const labelsHash = {
            [StateNames.TEMPLATES_PROFILES]: {
                icon: 'file-settings',
                label: l10nService.getMessage(l10nKeys.profilesLabel),
            },
            [StateNames.PROFILES_APPLICATION_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.applicationLabel),
            },
            [StateNames.PROFILES_NETWORK_PROFILE]: {
                label: l10nService.getMessage(globalL10nKeys.networkLabel),
            },
            [StateNames.PROFILES_PERSISTENCE_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.persistenceLabel),
            },
            [StateNames.PROFILES_HEALTH_MONITOR]: {
                label: l10nService.getMessage(l10nKeys.healthMonitorsLabel),
            },
            [StateNames.PROFILES_ANALYTICS_PROFILE]: {
                label: l10nService.getMessage(globalL10nKeys.analyticsLabel),
            },
            [StateNames.PROFILES_IPAM_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.ipamDnsProfilesLabel),
            },
            [StateNames.PROFILES_CUSTOM_IPAM_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.customIpamDnsLabel),
            },
            [StateNames.PROFILES_TRAFFIC_CLONE_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.trafficCloneLabel),
            },
            [StateNames.PROFILES_ICAP_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.icapProfileLabel),
            },
            [StateNames.TEMPLATES_POLICY]: {
                icon: 'list',
                label: l10nService.getMessage(l10nKeys.policiesLabel),
            },
            [StateNames.POLICY_NAT_POLICY]: {
                label: l10nService.getMessage(l10nKeys.natPolicyLabel),
            },
            [StateNames.POLICY_DNS_POLICY]: {
                label: l10nService.getMessage(globalL10nKeys.dnsPolicyLabel),
            },
            [StateNames.POLICY_L4_POLICY]: {
                label: l10nService.getMessage(l10nKeys.l4PolicySetLabel),
            },
            [StateNames.POLICY_NETWORK_SECURITY_POLICY]: {
                label: l10nService.getMessage(l10nKeys.networkSecurityPolicyLabel),
            },
            [StateNames.POLICY_HTTP_POLICY_SET]: {
                label: l10nService.getMessage(l10nKeys.httpPolicySetLabel),
            },
            [StateNames.POLICY_APPLICATION_INSIGHTS_POLICY]: {
                label: l10nService.getMessage(l10nKeys.applicationInsightsPolicyLabel),
            },
            [StateNames.POLICY_POSITIVE_SECURITY_POLICY]: {
                label: l10nService.getMessage(l10nKeys.positiveSecurityPolicyLabel),
            },
            [StateNames.TEMPLATES_GROUP]: {
                icon: 'file-group',
                label: l10nService.getMessage(l10nKeys.groupsLabel),
            },
            [StateNames.GROUP_IP_ADDR_GROUP]: {
                label: l10nService.getMessage(l10nKeys.ipGroupsLabel),
            },
            [StateNames.GROUP_STRING_GROUP]: {
                label: l10nService.getMessage(l10nKeys.stringGroupsLabel),
            },
            [StateNames.TEMPLATES_SSL]: {
                icon: 'shield-check',
                label: l10nService.getMessage(globalL10nKeys.securityLabel),
            },
            [StateNames.SSL_CERTIFICATE]: {
                label: l10nService.getMessage(l10nKeys.sslTlsCertificatesLabel),
            },
            [StateNames.SSL_SSL_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.sslTlsProfileLabel),
            },
            [StateNames.SSL_PKI_PROFILE]: {
                label: l10nService.getMessage(globalL10nKeys.pkiProfileLabel),
            },
            [StateNames.SSL_AUTH_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.authProfileLabel),
            },
            [StateNames.SSL_AUTH_MAPPING_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.authMappingProfileLabel),
            },
            [StateNames.SSL_TRUSTED_HOST_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.trustedHostProfileLabel),
            },
            [StateNames.SSL_JWT_SERVER_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.jwtServerProfileLabel),
            },
            [StateNames.SSL_CERTIFICATE_MANAGEMENT]: {
                label: l10nService.getMessage(l10nKeys.certificateManagementLabel),
            },
            [StateNames.SSL_IP_REPUTATION]: {
                label: l10nService.getMessage(globalL10nKeys.ipReputationLabel),
            },
            [StateNames.SSL_GEO_DB]: {
                label: l10nService.getMessage(globalL10nKeys.geoDbLabel),
            },
            [StateNames.SSL_HSM_GROUP]: {
                label: l10nService.getMessage(l10nKeys.hsmGroupsLabel),
            },
            [StateNames.SSL_SSO_POLICY]: {
                label: l10nService.getMessage(l10nKeys.ssoPolicyLabel),
            },
            [StateNames.SSL_CSRF_POLICY]: {
                label: l10nService.getMessage(l10nKeys.csrfPolicyPageLabel),
            },
            [StateNames.TEMPLATES_SCRIPTS]: {
                icon: 'code',
                label: l10nService.getMessage(l10nKeys.scriptsLabel),
            },
            [StateNames.SCRIPTS_DATASCRIPT_SET]: {
                label: l10nService.getMessage(l10nKeys.dataScriptsLabel),
            },
            [StateNames.SCRIPTS_CONTROLSCRIPTS_SET]: {
                label: l10nService.getMessage(l10nKeys.controlScriptsLabel),
            },
            [StateNames.SCRIPTS_PROTOCOL_PARSER_SCRIPTS]: {
                label: l10nService.getMessage(l10nKeys.protocolParserScriptsLabel),
            },
            [StateNames.TEMPLATES_AUTOSCALE]: {
                icon: 'cloud-scale',
                label: l10nService.getMessage(l10nKeys.autoScaleLabel),
            },
            [StateNames.AUTOSCALE_SERVER_AUTOSCALE]: {
                label: l10nService.getMessage(l10nKeys.serverAutoScaleLabel),
            },
            [StateNames.AUTOSCALE_LAUNCH_CONFIG]: {
                label: l10nService.getMessage(l10nKeys.launchConfigLabel),
            },
            [StateNames.AUTOSCALE_WEBHOOK]: {
                label: l10nService.getMessage(l10nKeys.webhookLabel),
            },
            [StateNames.AUTOSCALE_POOL_GROUP_DEPLOYMENT_POLICY]: {
                label: l10nService.getMessage(l10nKeys.poolGroupDeploymentLabel),
            },
            [StateNames.TEMPLATES_WAF]: {
                icon: 'firewall',
                label: l10nService.getMessage(globalL10nKeys.wafLabel),
            },
            [StateNames.WAF_WAF_POLICY]: {
                label: l10nService.getMessage(globalL10nKeys.wafPolicyLabel),
            },
            [StateNames.WAF_WAF_PROFILE]: {
                label: l10nService.getMessage(globalL10nKeys.wafProfileLabel),
            },
            [StateNames.WAF_APPLICATION_RULES]: {
                label: l10nService.getMessage(l10nKeys.applicationRulesLabel),
            },
            [StateNames.WAF_PSM]: {
                label: l10nService.getMessage(l10nKeys.positiveSecurityLabel),
            },
            [StateNames.WAF_WAF_CRS]: {
                label: l10nService.getMessage(l10nKeys.crsLabel),
            },
            [StateNames.TEMPLATES_BOT]: {
                icon: 'bot',
                label: l10nService.getMessage(globalL10nKeys.botLabel),
            },
            [StateNames.BOT_BOT_MANAGEMENT_POLICY]: {
                label: l10nService.getMessage(l10nKeys.botManagementPolicyListPageLabel),
            },
            [StateNames.BOT_BOT_MAPPING]: {
                label: l10nService.getMessage(l10nKeys.botMappingListPageLabel),
            },
            [StateNames.TEMPLATES_ERROR_PAGE]: {
                icon: 'exclamation-triangle',
                label: l10nService.getMessage(l10nKeys.errorPageLabel),
            },
            [StateNames.ERROR_PAGE_ERROR_PAGE_PROFILE]: {
                label: l10nService.getMessage(l10nKeys.errorPageProfileLabel),
            },
            [StateNames.ERROR_PAGE_ERROR_PAGE_BODY]: {
                label: l10nService.getMessage(l10nKeys.errorPageBodyLabel),
            },
            [StateNames.TEMPLATES_FILE_OBJECTS]: {
                icon: 'folder',
                label: l10nService.getMessage(globalL10nKeys.fileObjectsLabel),
            },
        };

        this.templatesStates = navigationService.getChildren(StateNames.TEMPLATES)
            .map(state => {
                const { label, icon } = labelsHash[state.name];

                return {
                    ...state,
                    label,
                    icon,
                    children: state.children?.map(childState => {
                        const childStateProps = labelsHash[childState.name];

                        return {
                            ...childState,
                            label: childStateProps?.label,
                            icon: childStateProps?.icon,
                        };
                    }),
                };
            });
    }
}
